'use client';

import {
  CvLayout,
  CvHeader,
  CvSection,
  CvTimeline,
  CvSkills,
  CvLanguages,
  CvInterests,
  CvFooter
} from '@/components/cv';
import { useData } from '@/i18n/LangContext';
import { useEffect, useRef, useContext } from 'react';
import { AppContext } from '@/context/AppContext';
import './printfix.css';
// Der Import von metadata wurde entfernt

// Der Export von Metadaten aus einer Client-Komponente ist nicht erlaubt
// export { metadata, viewport };

export default function CVPage() {
  const data = useData();
  const { ui } = data;
  // Lade die Daten
  const { intro } = data.about;
  const { 
    careerTimeline, 
    languages, 
    interests, 
    header, 
    exportButtonText 
  } = data.cv;
  
  const cvRef = useRef<HTMLDivElement>(null);
  const { setActivePage } = useContext(AppContext);
  
  // Setze die aktive Seite auf 'cv'
  useEffect(() => {
    setActivePage('cv');
  }, [setActivePage]);
  
  // Setze die Seite auf die richtige Größe für den Druck
  useEffect(() => {
    document.documentElement.classList.add('cv-page');
    
    return () => {
      document.documentElement.classList.remove('cv-page');
    };
  }, []);

  // Funktion zum Exportieren des Lebenslaufs als PDF
  const handleExportPdf = () => {
    window.print();
  };

  return (
    <>
      <div ref={cvRef}>
        <CvLayout>
          <CvHeader {...header} />
          
          <div className="flex justify-end mb-8 print:hidden">
            <button 
              onClick={handleExportPdf}
              className="px-4 py-2 bg-[rgba(198,156,109,0.2)] border border-[rgba(198,156,109,0.4)] text-[#C69C6D] rounded-md hover:bg-[rgba(198,156,109,0.3)] transition-colors font-medium"
            >
              {exportButtonText}
            </button>
          </div>
          
          <CvSection title={ui.cv.profile}>
            {intro.map((paragraph, index) => (
              <p key={index} className="text-white/80 mb-4 last:mb-0">
                {paragraph}
              </p>
            ))}
          </CvSection>
          
          <CvSection title={ui.cv.technicalSkills}>
            <CvSkills />
          </CvSection>
          
          <CvSection title={ui.cv.experience}>
            <CvTimeline items={careerTimeline} />
          </CvSection>
          
          <div className="print-together">
            <CvSection title={ui.cv.languages}>
              <CvLanguages languages={languages} />
            </CvSection>
            
            <CvSection title={ui.cv.interests}>
              <CvInterests interests={interests} />
            </CvSection>
          </div>
          
          <CvFooter />
        </CvLayout>
      </div>
      
      <style jsx global>{`
        @media print {
          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            overflow: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .cv-page {
            background: white !important;
            color: black !important;
          }
          
          @page {
            size: A4;
            margin: 1cm;
          }
          
          /* Navigation und Footer ausblenden */
          header.navigation, 
          .wrapper,
          footer.navigation,
          nav.navigation,
          .wrapper--top,
          .wrapper--bottom {
            display: none !important;
          }
          
          /* Verbesserte Druckstile für den Lebenslauf */
          .cv-page a {
            color: black !important;
            text-decoration: none !important;
          }
          
          .cv-page h1, .cv-page h2, .cv-page h3 {
            color: black !important;
            break-after: avoid !important;
          }
          
          /* Verhindert Seitenumbrüche innerhalb von Sektionen */
          .cv-page section {
            break-inside: avoid;
            page-break-inside: avoid;
            margin-bottom: 10px !important;
          }
          
          .cv-page .print-together {
            break-inside: avoid;
            page-break-inside: avoid;
            display: block;
          }
          
          .cv-page .print-together section {
            margin-bottom: 5px !important; /* Geringerer Abstand zwischen den Sektionen */
          }
          
          .cv-page .print-together section:last-child {
            margin-bottom: 0 !important;
          }
          
          .cv-page p {
            margin-bottom: 8px !important;
          }
          
          /* Seitenumbrüche zwischen größeren Abschnitten erlauben */
          .cv-page section + section {
            break-before: auto;
            page-break-before: auto;
          }
          
          /* Spezifische Abschnitte, die einen Seitenumbruch vor sich haben können */
          .cv-page section:nth-of-type(3) {
            break-before: page;
            page-break-before: always;
          }
          
          /* Sicherstellen, dass die print-together Gruppe nicht über zwei Seiten verteilt wird */
          .cv-page .print-together {
            break-before: page;
            page-break-before: always;
          }
          
          /* Entferne alle Hintergründe und setze sie auf weiß */
          .cv-page, 
          .cv-page *, 
          .cv-page *::before, 
          .cv-page *::after {
            background-color: white !important;
            background-image: none !important;
            backdrop-filter: none !important;
          }
          
          /* Braune Elemente spezifisch entfernen */
          .cv-page [class*="bg-[#C69C6D]"],
          .cv-page [class*="text-[#C69C6D]"],
          .cv-page [class*="fill-[#C69C6D]"],
          .cv-page [class*="border-[#C69C6D]"],
          .cv-page [class*="shadow-[0_0_10px_rgba(198,156,109"],
          .cv-page [class*="bg-[rgba(198,156,109"],
          .cv-page [class*="border-[rgba(198,156,109"] {
            background: white !important;
            color: black !important;
            fill: black !important;
            border-color: black !important;
            box-shadow: none !important;
            -webkit-text-fill-color: black !important;
          }
          
          /* Alle divs mit background-color */
          .cv-page div[style*="background-color"] {
            background-color: white !important;
          }
          
          /* Alle Elemente mit dem braunen Farbcode */
          .cv-page *[class*="C69C6D"], 
          .cv-page *[class*="A67C52"] {
            color: black !important;
            background: white !important;
            fill: black !important;
            border-color: rgba(0, 0, 0, 0.3) !important;
            box-shadow: none !important;
          }
          
          /* Textfarben auf schwarz setzen */
          .cv-page [class*="text-white"],
          .cv-page [class*="text-[#"],
          .cv-page p,
          .cv-page span:not([class*="border"]) {
            color: black !important;
          }
          
          /* Behalte nur Rahmen an Schmuckelementen bei */
          .cv-page [class*="border-["],
          .cv-page [class*="border"] {
            border-color: rgba(100, 100, 100, 0.3) !important;
            border-style: solid;
          }
          
          /* Entferne die äußere Border */
          .cv-page .max-w-4xl {
            border: none !important;
          }
          
          /* Schatten entfernen */
          .cv-page [class*="shadow-["],
          .cv-page [style*="box-shadow"] {
            box-shadow: none !important;
          }
          
          /* Fixes für transparenten Text */
          .cv-page [class*="bg-clip-text text-transparent"] {
            background-clip: unset !important;
            color: black !important;
            -webkit-text-fill-color: black !important;
          }
          
          /* Skill-Tags anpassen */
          .cv-page span[class*="rounded-full"] {
            background-color: white !important;
            color: black !important;
            border: 1px solid rgba(100, 100, 100, 0.5) !important;
          }
          
          /* Fix für die Container-Struktur */
          .cv-page .min-h-screen {
            min-height: auto !important;
          }
          
          /* Reduziere Abstände zwischen Abschnitten */
          .cv-page section + section {
            margin-top: 12px !important;
          }
          
          .cv-page h2 {
            margin-bottom: 8px !important;
          }
          
          /* Reduzierte Abstände im Header */
          .cv-page header {
            margin-bottom: 12px !important;
          }
          
          .cv-page header p {
            margin-bottom: 10px !important;
          }
          
          /* Abstände in den Schmuckelementen verringern */
          .cv-page [class*="absolute -bottom"] {
            margin-bottom: 0 !important;
            bottom: -2px !important;
          }
          
          /* Footer-Probleme beheben */
          .cv-page footer,
          .cv-page footer div,
          .cv-page footer p {
            background: white !important;
            color: black !important;
            border-color: rgba(100, 100, 100, 0.3) !important;
          }
          
          /* Sicherstellen, dass nach dem Footer keine Elemente mehr angezeigt werden */
          .cv-page footer::after {
            content: "";
            display: block;
            clear: both;
            height: 0;
            visibility: hidden;
          }
          
          /* Entfernt wirklich alle Hintergründe */
          body::after {
            content: "";
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: white !important;
            z-index: -1;
          }
          
          /* Setzt den Druckbereich auf die richtige Farbe */
          @page {
            background: white !important;
          }
        }
      `}</style>
    </>
  );
} 